import React from "react";
import { Link } from "react-router-dom";
import DiscoveryLayout, { Faq, SourceLink } from "../../components/discovery/DiscoveryLayout";
import CleanSweepsPlans from "../../components/discovery/CleanSweepsPlans";

const sources = {
  listing: "https://apps.shopify.com/pineraffle",
  widget: "https://pineraffle.helpscoutdocs.com/article/25-activate-configure-the-pineraffle-widget",
  campaign: "https://pineraffle.helpscoutdocs.com/article/6-ffle-using-the-pineraffle-app-step-by-step-instructions",
  participants: "https://pineraffle.helpscoutdocs.com/article/14-how-to-add-participants-to-a-raffle",
};

const pineRafflePlans = [
  { name: "Go", price: "9.99", features: "Storefront widget, automated emails and winner selection" },
  { name: "Grow", price: "19.99", features: "Product-specific ticket values, active-raffle editing, Klaviyo emails and participant export" },
  { name: "Pro", price: "39.99", features: "Grow features and multiple simultaneous active raffles" },
];

export default function PineRaffleComparison() {
  return (
    <DiscoveryLayout
      path="/compare/clean-sweeps-vs-pineraffle"
      title="Clean Sweeps vs PineRaffle for purchase-based giveaways"
      description="Compare Clean Sweeps and PineRaffle pricing, product entry rules, displays, referral rewards, AMOE and support for your Shopify giveaway."
      eyebrow="Shopify giveaway app comparison"
      sections={[
        { id: "which-app", label: "Which app fits?" },
        { id: "pricing", label: "Pricing and limits" },
        { id: "features", label: "Compare workflows" },
        { id: "launch", label: "Launch or switch" },
        { id: "frequently-asked-questions", label: "Common questions" },
      ]}
    >
      <section id="which-app">
        <h2>Match the app to your merchandise giveaway</h2>
        <p>
          Clean Sweeps is a strong choice when you want purchase-based
          sweepstakes to be part of the shopping experience: entries per dollar,
          product entry displays, cart estimates, customer totals and rewards
          connected to qualifying purchases. Start with the entry rules and
          displays your first campaign needs, then select the plan that covers
          those features and your monthly processed orders.
        </p>
        <p>
          PineRaffle deserves consideration when a lower starting paid price is
          the priority. Its listing starts at $9.99 per month and describes
          purchase-based entries, product-specific tickets and a storefront
          widget. That starting price is lower than Clean Sweeps&apos; first paid
          tier; Clean Sweeps also has a Free plan with limited capacity.{" "}
          <SourceLink href={sources.listing}>PineRaffle listing</SourceLink>.
        </p>
        <p>
          For a growing merchandise brand, compare the details of the customer
          journey as well as the subscription price. How does a shopper see
          potential entries before buying? Where can a customer check awarded
          entries afterward? How will your team handle a free-entry request?
          Those are practical reasons to evaluate Clean Sweeps&apos; displays,
          rewards and AMOE workflow at the appropriate plan level.
        </p>
      </section>

      <section id="pricing">
        <h2>Pricing and capacity without hidden assumptions</h2>
        <CleanSweepsPlans />
        <h3>PineRaffle monthly plans</h3>
        <div className="discovery-table-scroll" role="region" aria-label="PineRaffle monthly pricing" tabIndex={0}>
          <table>
            <caption>PineRaffle monthly USD prices and listed plan highlights</caption>
            <thead>
              <tr><th scope="col">Plan</th><th scope="col">Monthly price</th><th scope="col">Listed highlights</th></tr>
            </thead>
            <tbody>
              {pineRafflePlans.map((plan) => (
                <tr key={plan.name}>
                  <th scope="row">{plan.name}</th>
                  <td>${plan.price}/month</td>
                  <td>{plan.features}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          PineRaffle lists a seven-day trial. A monthly processed-order limit
          is not specified in the listing reviewed; do not interpret that as
          a promise of unlimited capacity. Confirm current limits with its
          support team. Pricing above uses the current App Store plan names.{" "}
          <SourceLink href={sources.listing}>PineRaffle pricing source</SourceLink>.
        </p>
        <p>
          Clean Sweeps&apos; capacity is explicitly measured in processed orders
          per month. Its Free plan is suitable for a small start with the product
          widget, but in-app winner selection begins on Basic. Gold adds cart,
          guest lookup and post-checkout displays. Platinum adds referral,
          membership and email rewards; Diamond adds AMOE tracking and broader
          white-glove services. Select a plan for both capacity and functionality
          instead of assuming every feature is included at the lowest price.
        </p>
      </section>

      <section id="features">
        <h2>Compare the workflows customers and staff will use</h2>
        <div className="discovery-table-scroll" role="region" aria-label="Clean Sweeps and PineRaffle feature comparison" tabIndex={0}>
          <table>
            <caption>Verified features and questions to confirm before subscribing</caption>
            <thead>
              <tr><th scope="col">Workflow</th><th scope="col">Clean Sweeps</th><th scope="col">PineRaffle</th></tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Purchase-entry rules</th>
                <td>Entries per dollar, product rules and bonuses; special multipliers on Platinum and above.</td>
                <td>Order-value and product-based entry types. <SourceLink href={sources.campaign}>Campaign guide</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Storefront presentation</th>
                <td>Product widget from Free; account from Basic; cart, guest lookup and post-checkout summary from Gold. Checkout extensions require Platinum and a compatible Shopify Plus checkout.</td>
                <td>Customizable storefront panel with prize details, entry instructions and order-value progress messaging. <SourceLink href={sources.widget}>Widget guide</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Referral and membership rewards</th>
                <td>Referral codes and membership entry rules on Platinum and above.</td>
                <td>Not specified in the sources reviewed; confirm the exact workflow with PineRaffle.</td>
              </tr>
              <tr>
                <th scope="row">Email rewards</th>
                <td>Confirmed email signup entries, signup form and branded email service on Platinum and above.</td>
                <td>An email-based entry type is documented; confirm the current plan required. <SourceLink href={sources.campaign}>Entry types</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Entrant management</th>
                <td>Manage campaign entrants; winner selection on Basic and above.</td>
                <td>Supports manually adding participants. <SourceLink href={sources.participants}>Participant guide</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">AMOE review and approval</th>
                <td>Diamond includes a no-purchase entry request form with pending review and approval.</td>
                <td>A dedicated AMOE review-and-approval workflow is not specified in the sources reviewed.</td>
              </tr>
              <tr>
                <th scope="row">First-campaign setup</th>
                <td>Free configuration and standard-widget placement for new subscribers, including Free, within the selected plan&apos;s features.</td>
                <td>An equivalent included setup package is not specified in the sources reviewed.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          “Not specified” means the reviewed pages did not establish that
          capability or offer; it does not mean the competitor cannot provide it.
          For Clean Sweeps, inspect the <Link to="/#pricing">plan matrix</Link>,{" "}
          <Link to="/sweepstakes-entry-widgets">widget reference</Link> and{" "}
          <Link to="/setup?tab=amoe">AMOE setup tutorial</Link>. Ask each provider
          to demonstrate any workflow that is essential to your launch.
        </p>
        <p>
          Clean Sweeps is particularly useful when you want entry information at
          several points in the purchase journey. A product widget can explain
          the offer while a customer browses; a cart display can estimate the
          current basket; an account or guest lookup can help customers check
          their totals. Review these as separate placements when planning your
          theme rather than treating “has a widget” as a complete specification.
        </p>
      </section>

      <section id="launch">
        <h2>Evaluate with a real campaign example</h2>
        <p>
          Choose one eligible merchandise product, one excluded product and a
          simple entry rate. Calculate the expected entries before testing. Then
          repeat with a bonus product, an existing customer and a guest customer.
          Check the displayed estimate against the awarded entries and the
          customer&apos;s final total. Use this same small test set for both apps;
          it makes differences in setup and presentation easier to assess.
        </p>
        <p>
          Plan the no-purchase entry process alongside purchase entries. Specify
          where people find instructions, what information is collected, who
          reviews requests and how accepted entries reach the entrant records.
          A form is one operational component. It does not replace official
          rules, legal review or any administration services your promotion
          requires. Clean Sweeps&apos; built-in AMOE feature is on Diamond, so
          include that plan requirement when budgeting for this workflow.
        </p>
        <p>
          If you are moving from PineRaffle, preserve records from the previous
          campaign and choose a clear start for the next one. Keep historical
          migration separate from setting up a new promotion. Clean Sweeps&apos;
          free first-campaign setup offer below covers configuration and eligible
          standard widgets, with no review requirement. It does not include
          historical data migration, custom development or ongoing management.
        </p>
      </section>

      <Faq items={[
          {
            question: "Is Clean Sweeps cheaper than PineRaffle?",
            answer: "There is no single answer across all plans. Clean Sweeps has a Free plan with a small processed-order allowance, while PineRaffle has a lower starting paid subscription. Compare the plan that actually covers your campaign, required displays, rewards and capacity.",
          },
          {
            question: "Why choose Clean Sweeps for a merchandise giveaway?",
            answer: "Choose it when configurable purchase entries, product and cart displays, customer entry totals and a path to referral, membership and email rewards fit your campaign. New subscribers can also request the scoped first-campaign setup offer, including on Free. Feature access depends on the selected plan.",
          },
          {
            question: "Does PineRaffle's raffle terminology determine what I can legally run?",
            answer: "No. An app's terminology does not determine your promotion's legal classification. Have the campaign mechanics, official rules and entry methods reviewed for the places where you intend to operate, regardless of which software you select.",
          },
      ]} />
    </DiscoveryLayout>
  );
}
